import { useRef, useState } from "react";
import { EmailGate } from "../components/EmailGate";
import {
  useWalletProviders,
  WalletPicker,
  walletOptions,
  type WalletOption,
} from "../components/WalletPicker";
import { Navigate, useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { isAuthenticated, loginWithWallet, type WalletLoginStage } from "../lib/auth";
import { clearEmailSession, getStoredAccess, getVerifiedEmail } from "../lib/emailGate";
import { getEmailGate, getNetworkLabel } from "../lib/runtimeConfig";
import { errorReporter } from "../lib/errorReporter";
import {
  cancelConnect,
  CHAIN_UNKNOWN,
  explainWalletError,
  getSelectedRdns,
  hasWallet,
  isConnectionCancelled,
  isUserRejected,
  selectProvider,
  toWalletError,
  WALLETCONNECT_RDNS,
} from "../lib/wallet";


/**
 * A function, not a table: the network line names the deployment's chain, and
 * over WalletConnect, where nothing opens on screen, the lines name a phone app.
 */
function stageText(stage: WalletLoginStage, viaWalletConnect: boolean): string {
  const where = viaWalletConnect ? "your wallet app" : "your wallet";
  switch (stage) {
    case "connecting":
      return `Waiting for ${where}…`;
    case "pairing":
      return "Scan the QR code with your wallet app…";
    case "network": {
      const network = getNetworkLabel();
      return network === null ? "Switching network…" : `Switching to ${network}…`;
    }
    case "nonce":
      return "Requesting a sign-in challenge…";
    case "signing":
      return `Confirm the signature in ${where}…`;
    case "verifying":
      return "Verifying signature…";
  }
}

type WalletState =
  | { kind: "idle" }
  | { kind: "busy"; stage: WalletLoginStage; rdns: string | null }
  | { kind: "rejected" }
  | { kind: "error"; message: string };

/** Wallet glyph for the primary button. */
function WalletIcon() {
  return (
    <svg
      className="btn-glyph"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 5.5A2 2 0 014.5 3.5h8a1.5 1.5 0 011.5 1.5v1" />
      <rect x="2.5" y="5.5" width="13" height="9" rx="2" />
      <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Outside the App shell. Wallet and SIWE only: a mainnet venue signs users in with their keys. */
export function LoginPage() {
  const navigate = useNavigate();
  const providers = useWalletProviders();
  const options = walletOptions(providers);
  // WalletConnect alone is enough to sign in; `hasWallet()` still covers the
  // pre-6963 `window.ethereum` case, which announces nothing.
  const walletAvailable = options.length > 0 || hasWallet();

  const [walletState, setWalletState] = useState<WalletState>({ kind: "idle" });
  const [selectedRdns, setSelectedRdns] = useState<string | null>(() => getSelectedRdns());
  const [pickerOpen, setPickerOpen] = useState(false);
  // Bumped by every attempt and by Cancel, so an abandoned relay call cannot
  // overwrite a later sign-in with an extension.
  const attempt = useRef(0);
  // With the email gate on, the wallet button appears only once this browser's
  // email is verified and allowlist-approved.
  const [approvedEmail, setApprovedEmail] = useState<string | null>(() =>
    getStoredAccess() === "approved" ? getVerifiedEmail() : null,
  );
  const emailGateOpen = getEmailGate().enabled && approvedEmail === null;

  // Already signed in — the login page redirects to the dashboard.
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  const walletBusy = walletState.kind === "busy";
  const pinned = options.find((o) => o.rdns === selectedRdns) ?? null;
  const sole = options.length === 1 ? (options[0] ?? null) : null;
  // Several ways in and no remembered choice: the user has to say which.
  const showPicker = options.length > 1 && (pinned === null || pickerOpen);
  const target: WalletOption | null = pinned ?? sole;
  const targetName = target?.name;

  function reportWalletFailure(err: unknown) {
    if (isConnectionCancelled(err)) {
      // The QR sheet was closed: nothing was rejected or signed, so no banner, never reported.
      setWalletState({ kind: "idle" });
      return;
    }
    if (isUserRejected(err)) {
      // Expected outcome — the user declined in their wallet. Never reported.
      setWalletState({ kind: "rejected" });
      return;
    }
    if (err instanceof ApiError) {
      // Already reported by the api layer (401/404 are expectedStatuses).
      setWalletState({
        kind: "error",
        message:
          err.status === 401
            ? "Signature was not accepted — try again"
            : "Wallet sign-in is unavailable right now",
      });
      return;
    }
    const walletError = toWalletError(err);
    if (walletError.code === CHAIN_UNKNOWN) {
      // Expected: the descriptor did not load at boot, and that was reported then.
      setWalletState({ kind: "error", message: explainWalletError(walletError) });
      return;
    }
    errorReporter.captureException(walletError, {
      source: "wallet",
      code: walletError.code,
      stage: "sign-in",
    });
    // The provider's own words: a generic message makes this undiagnosable.
    setWalletState({ kind: "error", message: explainWalletError(walletError) });
  }

  /**
   * Not async: `loginWithWallet` sends `eth_requestAccounts` before its first
   * await, and the wallet opens its popup only while the click's user gesture is
   * on the stack. Awaiting anything here leaves the extension open and silent.
   */
  function handleConnectWallet(rdns?: string) {
    if (walletBusy) return;
    if (rdns !== undefined) {
      selectProvider(rdns); // synchronous localStorage write
      setSelectedRdns(rdns);
      setPickerOpen(false);
    }
    const id = (attempt.current += 1);
    setWalletState({ kind: "busy", stage: "connecting", rdns: rdns ?? getSelectedRdns() });
    loginWithWallet(
      (stage) => {
        if (attempt.current !== id) return;
        setWalletState((prev) =>
          prev.kind === "busy" ? { kind: "busy", stage, rdns: prev.rdns } : prev,
        );
      },
      rdns,
    ).then(
      () => {
        if (attempt.current === id) navigate("/", { replace: true });
      },
      (err: unknown) => {
        if (attempt.current === id) reportWalletFailure(err);
      },
    );
  }

  /**
   * WalletConnect only: over the relay nothing on screen can be dismissed, and a
   * signature may wait three minutes. Offered while pairing too, since a failed
   * import of AppKit's lazy modal leaves no QR sheet and `enable()` waiting out
   * the five-minute proposal TTL. `cancelConnect()`, not `disconnect()`: the
   * pairing has no deadline, and while it is outstanding `wallet.ts`'s in-flight
   * guard makes every wallet button answer "a prompt is already open".
   */
  function handleCancelWallet() {
    attempt.current += 1;
    cancelConnect();
    setWalletState({ kind: "idle" });
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <img className="login-logo" src="/aile-logo.svg" alt="Aile" />
        <h1 className="login-title">Sign in</h1>
        <p className="login-sub">
          {emailGateOpen
            ? "AileFX is in private preview. Verify your email to continue."
            : "Connect your wallet to trade on AileFX."}
        </p>

        {emailGateOpen ? (
          <EmailGate onVerified={(email) => setApprovedEmail(email)} />
        ) : showPicker ? (
          <WalletPicker
            options={options}
            selectedRdns={selectedRdns}
            onPick={handleConnectWallet}
            disabled={walletBusy}
          />
        ) : (
          <button
            type="button"
            className="btn btn-primary login-wallet-button"
            onClick={() => handleConnectWallet(target?.rdns)}
            disabled={walletBusy}
          >
            <WalletIcon />
            {walletBusy
              ? "Connecting…"
              : targetName === undefined
                ? "Connect wallet"
                : `Connect ${targetName}`}
          </button>
        )}

        {options.length > 1 && !showPicker && (
          <button
            type="button"
            className="link-button login-wallet-switch"
            onClick={() => setPickerOpen(true)}
          >
            Use a different wallet
          </button>
        )}

        {!emailGateOpen && getEmailGate().enabled && approvedEmail !== null && (
          <p className="login-wallet-note">
            Verified as <strong>{approvedEmail}</strong>
            {" · "}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                clearEmailSession();
                setApprovedEmail(null);
              }}
              disabled={walletBusy}
            >
              change
            </button>
          </p>
        )}

        {walletState.kind === "busy" && (
          <div className="login-wallet-status" role="status">
            <span className="spinner" aria-hidden="true" />
            {stageText(walletState.stage, walletState.rdns === WALLETCONNECT_RDNS)}
            {walletState.rdns === WALLETCONNECT_RDNS && (
              <button type="button" className="link-button" onClick={handleCancelWallet}>
                Cancel
              </button>
            )}
          </div>
        )}

        {walletState.kind === "rejected" && (
          <div className="inline-banner inline-banner-warn" role="alert">
            Signature rejected — try again
          </div>
        )}

        {walletState.kind === "error" && (
          <div className="inline-banner inline-banner-error" role="alert">
            {walletState.message}
          </div>
        )}

        {!walletAvailable && (
          <div className="inline-banner inline-banner-muted" role="note">
            No wallet detected — install{" "}
            <a href="https://metamask.io" target="_blank" rel="noreferrer noopener">
              metamask.io
            </a>{" "}
            to sign in.
          </div>
        )}

        {emailGateOpen && (
          <p className="login-foot">
            Need access? <a href="mailto:hello@aile.tech">hello@aile.tech</a>
          </p>
        )}
      </div>
    </div>
  );
}
